import {
  CapacityContributionStatus,
  WorkerShiftStatus,
  type CapacityContributionRecordPartner,
  type WorkerShiftRecord
} from '@navio/nsdk';
import { BUSINESS_TIME_ZONE, formatClockTime, parseHhmm, zonedWallClockIso } from 'noddi-util';

/** The absence's dates and, for a partial one, its first day's start and last day's end ("HH:mm[:ss]"). */
export type AbsenceWindow = {
  startDate: string;
  endDate: string;
  startTime?: string | null;
  endTime?: string | null;
};

/** What approving an absence does to one worker shift it overlaps. */
export type AbsenceShiftEffect = {
  workerShift: WorkerShiftRecord;
  /** The absence covers the planned shift end to end, so the shift is canceled. */
  cancelsShift: boolean;
  /**
   * Live bookable time without a route that is stopped. A partial absence stops every bookable time
   * it overlaps whole, not only the hours the worker is away.
   */
  stopped: CapacityContributionRecordPartner[];
  /** Bookable time with a route, whatever its status: left as it is for a planner to reassign. */
  routed: CapacityContributionRecordPartner[];
};

const NOT_LIVE_STATUSES: CapacityContributionStatus[] = [
  CapacityContributionStatus.DECLINED,
  CapacityContributionStatus.WITHDRAWN
];

/** `YYYY-MM-DD` of the calendar day after `ymd`; calendar arithmetic, so no zone or DST applies. */
const nextDay = (ymd: string) => {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day) + 1)).toISOString().slice(0, 10);
};

/** A wall-clock time on `ymd` as the instant the backend means: the absence carries no zone. */
const onBusinessClock = (ymd: string, time: string) => {
  const clock = parseHhmm(formatClockTime(time));
  // An unreadable time overlaps nothing, as an invalid date always did.
  return clock ? new Date(zonedWallClockIso(ymd, clock, BUSINESS_TIME_ZONE)) : new Date(Number.NaN);
};

/**
 * The absence as the backend stores it, on the business wall clock (not the browser's): a whole-day
 * absence from midnight of its first day to midnight after its last, a partial one from its start
 * time on the first day to its end time on the last.
 */
const absenceInterval = ({ startDate, endDate, startTime, endTime }: AbsenceWindow) => {
  if (startTime && endTime) {
    return { startsAt: onBusinessClock(startDate, startTime), endsAt: onBusinessClock(endDate, endTime) };
  }
  return { startsAt: onBusinessClock(startDate, '00:00'), endsAt: onBusinessClock(nextDay(endDate), '00:00') };
};

/**
 * Per worker shift, what approving (or rescheduling an approved) absence does. Mirrors the backend's
 * `_withdraw_empty_overlapping_shifts`: a planned shift the absence covers end to end is canceled
 * with its bookable time; otherwise the shift keeps its hours and only the bookable time the
 * absence overlaps is stopped. Routed bookable time (`hasRoute`) is never stopped.
 */
export const absenceShiftEffects = ({
  absence,
  workerShifts
}: {
  absence: AbsenceWindow;
  workerShifts: WorkerShiftRecord[];
}): AbsenceShiftEffect[] => {
  const { startsAt, endsAt } = absenceInterval(absence);
  const overlaps = (item: { startsAt: string; endsAt: string }) =>
    new Date(item.startsAt) < endsAt && new Date(item.endsAt) > startsAt;

  return (
    workerShifts
      .filter(overlaps)
      .map((workerShift) => {
        const cancelsShift =
          workerShift.status === WorkerShiftStatus.PLANNED &&
          new Date(workerShift.startsAt) >= startsAt &&
          new Date(workerShift.endsAt) <= endsAt;
        const affected = cancelsShift
          ? workerShift.capacityContributions
          : workerShift.capacityContributions.filter(overlaps);
        return {
          workerShift,
          cancelsShift,
          stopped: affected.filter(
            (contribution) => !contribution.hasRoute && !NOT_LIVE_STATUSES.includes(contribution.status.value)
          ),
          routed: affected.filter((contribution) => contribution.hasRoute)
        };
      })
      // A planned shift is listed even when nothing in it changes: that it keeps its hours is the answer.
      .filter((effect) => effect.workerShift.status === WorkerShiftStatus.PLANNED || effect.routed.length > 0)
      .sort((left, right) => left.workerShift.startsAt.localeCompare(right.workerShift.startsAt))
  );
};
